/**
 * Validazione difensiva del manifest ricevuto dal server (README spec, §1).
 *  - spec_version sconosciuta  -> null (il chiamante continua col manifest precedente)
 *  - video senza duration_ms valida -> asset scartato (le voci che lo usano diventano "asset mancante")
 *  - voci malformate -> scartate
 * Non riordina mai `schedule`.
 */
import type { Manifest, ManifestAsset, ScheduleEntry } from './types';
import { assertValidTimeZone } from './engine/tz';

export interface SanitizeResult {
  manifest: Manifest | null;
  warnings: string[];
}

function isObj(x: unknown): x is Record<string, unknown> {
  return typeof x === 'object' && x !== null && !Array.isArray(x);
}
const isStr = (x: unknown): x is string => typeof x === 'string' && x.length > 0;
const isPosInt = (x: unknown): x is number => typeof x === 'number' && Number.isInteger(x) && x > 0;
const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;
const TIME_RE = /^\d{2}:\d{2}:\d{2}$/;

function sanitizeAsset(raw: unknown, warnings: string[]): ManifestAsset | null {
  if (!isObj(raw) || !isStr(raw.id) || (raw.kind !== 'video' && raw.kind !== 'image') || !isStr(raw.url)) {
    warnings.push('asset malformato scartato');
    return null;
  }
  if (typeof raw.sha256 !== 'string' || !/^[0-9a-f]{64}$/.test(raw.sha256)) {
    warnings.push(`asset ${raw.id}: sha256 non valido`);
    return null;
  }
  if (raw.kind === 'video' && !isPosInt(raw.duration_ms)) {
    warnings.push(`video ${raw.id} senza duration_ms valida: scartato`);
    return null;
  }
  return {
    id: raw.id,
    kind: raw.kind,
    url: raw.url,
    sha256: raw.sha256,
    size: typeof raw.size === 'number' ? raw.size : 0,
    mime: typeof raw.mime === 'string' ? raw.mime : '',
    width: typeof raw.width === 'number' ? raw.width : 0,
    height: typeof raw.height === 'number' ? raw.height : 0,
    duration_ms: raw.kind === 'video' ? (raw.duration_ms as number) : null,
  };
}

function sanitizeEntry(raw: unknown, warnings: string[]): ScheduleEntry | null {
  if (!isObj(raw) || !isStr(raw.id) || !isStr(raw.asset_id)) {
    warnings.push('voce di schedule malformata scartata');
    return null;
  }
  const id = raw.id;
  const okDates = typeof raw.start_date === 'string' && DATE_RE.test(raw.start_date)
    && (raw.end_date === null || (typeof raw.end_date === 'string' && DATE_RE.test(raw.end_date)));
  const okTimes = typeof raw.start_time === 'string' && TIME_RE.test(raw.start_time)
    && (raw.end_time === null || (typeof raw.end_time === 'string' && TIME_RE.test(raw.end_time)));
  const okWeekdays = Array.isArray(raw.weekdays) && raw.weekdays.length === 7 && raw.weekdays.every((b) => typeof b === 'boolean');
  if (!okDates || !okTimes || !okWeekdays || !isPosInt(raw.week_repeat) || !isPosInt(raw.repeat)
    || !(raw.display_ms === null || isPosInt(raw.display_ms))) {
    warnings.push(`voce ${id} non valida: scartata`);
    return null;
  }
  return {
    id,
    asset_id: raw.asset_id,
    start_date: raw.start_date as string,
    start_time: raw.start_time as string,
    end_date: raw.end_date as string | null,
    end_time: raw.end_time as string | null,
    weekdays: raw.weekdays as boolean[],
    week_repeat: raw.week_repeat,
    order: typeof raw.order === 'number' ? raw.order : 0,
    repeat: raw.repeat,
    display_ms: raw.display_ms as number | null,
  };
}

export function sanitizeManifest(raw: unknown): SanitizeResult {
  const warnings: string[] = [];
  if (!isObj(raw)) return { manifest: null, warnings: ['manifest non è un oggetto'] };
  if (raw.spec_version !== 1) return { manifest: null, warnings: [`spec_version non supportata: ${String(raw.spec_version)}`] };
  if (!isStr(raw.version) || !isStr(raw.timezone) || !Array.isArray(raw.assets) || !Array.isArray(raw.schedule)) {
    return { manifest: null, warnings: ['manifest incompleto'] };
  }
  try {
    assertValidTimeZone(raw.timezone);
  } catch {
    return { manifest: null, warnings: [`fuso sconosciuto: ${raw.timezone}`] };
  }
  const sg = raw.sync_group;
  let syncGroup: Manifest['sync_group'] = null;
  if (sg !== null && sg !== undefined) {
    if (!isObj(sg) || typeof sg.id !== 'number' || typeof sg.anchor !== 'string' || Number.isNaN(Date.parse(sg.anchor))) {
      return { manifest: null, warnings: ['sync_group non valido'] };
    }
    syncGroup = { id: sg.id, anchor: sg.anchor };
  }
  const assets = raw.assets.map((a) => sanitizeAsset(a, warnings)).filter((a): a is ManifestAsset => a !== null);
  const schedule = raw.schedule.map((s) => sanitizeEntry(s, warnings)).filter((s): s is ScheduleEntry => s !== null);
  const dev: Record<string, unknown> = isObj(raw.device) ? raw.device : {};
  const orientation = dev.orientation === 90 || dev.orientation === 180 || dev.orientation === 270 ? dev.orientation : 0;
  const manifest: Manifest = {
    spec_version: 1,
    version: raw.version,
    generated_at: typeof raw.generated_at === 'string' ? raw.generated_at : '',
    timezone: raw.timezone,
    device: {
      id: typeof dev.id === 'number' ? dev.id : 0,
      name: typeof dev.name === 'string' ? dev.name : '',
      orientation,
    },
    sync_group: syncGroup,
    fallback_asset_id: typeof raw.fallback_asset_id === 'string' ? raw.fallback_asset_id : null,
    assets,
    schedule,
  };
  if (typeof raw.urls_expire_at === 'string') manifest.urls_expire_at = raw.urls_expire_at;
  return { manifest, warnings };
}
